import React from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import type { AppearanceType } from '@atlaskit/avatar/types';

import { messages } from '../../../../../messages';
import { useAIAnswerContext } from '../../../../ai-answer-context';
import { NLPSearchResultContainerDataTypeContainerType } from '../../../../nlp-search-result-container-data-type-container-type';
import { onWhoPrimaryCoordinationPillClick } from '../../../../on-who-primary-coordination-pill-click';
import { onWhoPrimaryCoordinationPillContextMenuClick } from '../../../../on-who-primary-coordination-pill-context-menu-click';
import type {
	NLPSearchWhoResultCollaboratorDataType,
	NLPSearchWhoResultContainerDataType,
	NLPSearchWhoResultTeamDataType,
} from '../../../../types';

import { AIWhoAnswerSectionHeader } from './ai-who-answer-section-header';
import { AIWhoAnswerSectionResult } from './ai-who-answer-section-result';
import { AIWhoAnswerSectionResultInfoHeader } from './ai-who-answer-section-result-info-header';
import { AIWhoAnswerSectionResultInfoWrapper } from './ai-who-answer-section-result-info-wrapper';
import { AIWhoAnswerSectionResultMetadata } from './ai-who-answer-section-result-metadata';
import { AIWhoAnswerSectionResultsWrapper } from './ai-who-answer-section-results-wrapper';
import { AIWhoAnswerSectionWrapper } from './ai-who-answer-section-wrapper';

const WHO_SECTION_MAX_ITEMS = 3;

type AIAnswerWhoSectionProps = {
	sectionId: string;
	sectionHeader: MessageDescriptor;
	sectionResults:
		| Array<
				| NLPSearchWhoResultContainerDataType
				| NLPSearchWhoResultCollaboratorDataType
				| NLPSearchWhoResultTeamDataType
		  >
		| undefined;
	avatarAppearance: AppearanceType;
};

export const AIAnswerWhoSection = ({
	sectionId,
	sectionHeader,
	sectionResults,
	avatarAppearance,
}: AIAnswerWhoSectionProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { commonAttributes, fireAnalyticsEvent, onNavigate } = useAIAnswerContext();

	const getSectionResultMetadata = (
		sectionResult:
			| NLPSearchWhoResultContainerDataType
			| NLPSearchWhoResultCollaboratorDataType
			| NLPSearchWhoResultTeamDataType,
	): string => {
		if ('containerType' in sectionResult) {
			switch (sectionResult.containerType) {
				case NLPSearchResultContainerDataTypeContainerType.ConfluenceSpace:
					return 'Confluence';
				case NLPSearchResultContainerDataTypeContainerType.JiraProject:
					return 'Jira Software';
				default:
					return '';
			}
		}
		if ('title' in sectionResult) {
			return sectionResult.title || '';
		}
		if ('memberCount' in sectionResult) {
			return formatMessage(messages.ai_answer_who_question_team_section_member_count, {
				count: sectionResult.memberCount,
			});
		}
		return '';
	};

	return sectionResults && sectionResults.length ? (
		<AIWhoAnswerSectionWrapper>
			<AIWhoAnswerSectionHeader>{formatMessage(sectionHeader)}</AIWhoAnswerSectionHeader>
			<AIWhoAnswerSectionResultsWrapper>
				{sectionResults.slice(0, WHO_SECTION_MAX_ITEMS).map((sectionResult) => (
					<AIWhoAnswerSectionResult
						key={sectionResult.name}
						href={sectionResult.url || undefined}
						onClick={(event) => {
							onNavigate(event);
							fireAnalyticsEvent(
								onWhoPrimaryCoordinationPillClick({
									...commonAttributes,
									resultSection: sectionId,
									resultTitle: sectionResult.name,
								}),
							);
						}}
						onContextMenu={() => {
							fireAnalyticsEvent(
								onWhoPrimaryCoordinationPillContextMenuClick({
									...commonAttributes,
									resultSection: sectionId,
									resultTitle: sectionResult.name,
								}),
							);
						}}
					>
						<Avatar
							appearance={avatarAppearance}
							src={
								(sectionResult as NLPSearchWhoResultContainerDataType).iconUrl ||
								(sectionResult as NLPSearchWhoResultCollaboratorDataType).picture ||
								(sectionResult as NLPSearchWhoResultTeamDataType).avatarUrl ||
								undefined
							}
							size="medium"
						/>
						<AIWhoAnswerSectionResultInfoWrapper>
							<AIWhoAnswerSectionResultInfoHeader>
								{sectionResult.name}
							</AIWhoAnswerSectionResultInfoHeader>
							<AIWhoAnswerSectionResultMetadata>
								{getSectionResultMetadata(sectionResult)}
							</AIWhoAnswerSectionResultMetadata>
						</AIWhoAnswerSectionResultInfoWrapper>
					</AIWhoAnswerSectionResult>
				))}
			</AIWhoAnswerSectionResultsWrapper>
		</AIWhoAnswerSectionWrapper>
	) : null;
};
